import { useEffect, useState, useCallback } from "react";
import { Select, Tag } from "antd";
import { Button, Input, Table } from "@/frontend/components/common";
import {
  tournamentFormatService,
  matchTeamService,
  matchScoreService,
  disciplineService,
  subCategoryService,
} from "@/frontend/services";
import type {
  TournamentFormatResponseDTO,
  MatchTeamResponseDTO,
  MatchScoreResponseDTO,
  DisciplineResponseDTO,
  SubCategoryResponseDTO,
} from "@/shared/dto";
import { FaEdit, FaSearch, FaTimes, FaPlus } from "react-icons/fa";
import { MatchScoreModal } from "./components/MatchScoreModal";
import { MatchCreateModal } from "./components/MatchCreateModal";
import { formatDate } from "@/shared/utils";
import dayjs from "dayjs";

async function fetchAllMatchTeams() {
  const firstPage = await matchTeamService.getPaginate({ page: 1, pageSize: 100 });
  if (!firstPage.success || !firstPage.data) {
    return [];
  }

  const teams = [...firstPage.data.data];
  const totalPages = firstPage.data.pagination.totalPages;

  if (totalPages > 1) {
    const remainingPages = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, index) =>
        matchTeamService.getPaginate({ page: index + 2, pageSize: 100 }),
      ),
    );

    for (const page of remainingPages) {
      if (page.success && page.data) {
        teams.push(...page.data.data);
      }
    }
  }

  return teams;
}

async function fetchAllTournamentFormats() {
  const firstPage = await tournamentFormatService.getPaginate({ page: 1, pageSize: 100 });
  if (!firstPage.success || !firstPage.data) {
    return [];
  }

  const formats = [...firstPage.data.data];
  const totalPages = firstPage.data.pagination.totalPages;

  if (totalPages > 1) {
    const remainingPages = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, index) =>
        tournamentFormatService.getPaginate({ page: index + 2, pageSize: 100 }),
      ),
    );

    for (const page of remainingPages) {
      if (page.success && page.data) {
        formats.push(...page.data.data);
      }
    }
  }

  return formats;
}

async function fetchAllSubCategories() {
  const firstPage = await subCategoryService.getPaginate({ page: 1, pageSize: 100 });
  if (!firstPage.success || !firstPage.data) {
    return [];
  }

  const subCategories = [...firstPage.data.data];
  const totalPages = firstPage.data.pagination.totalPages;

  if (totalPages > 1) {
    const remainingPages = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, index) =>
        subCategoryService.getPaginate({ page: index + 2, pageSize: 100 }),
      ),
    );

    for (const page of remainingPages) {
      if (page.success && page.data) {
        subCategories.push(...page.data.data);
      }
    }
  }

  return subCategories;
}

function normalizeSearchValue(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

interface MatchRow {
  key: string;
  tournamentFormatId: string;
  eventName: string;
  sport: string;
  subCategoryId?: string;
  matchOrder: number;
  homeTeamId: string;
  awayTeamId: string;
  date?: string;
  time?: string;
  status?: string;
  liveMinutes?: string;
}

interface ScoreModalState {
  tournamentFormatId: string;
  matchOrder: number;
  matchLabel: string;
  homeTeamName: string;
  awayTeamName: string;
}

export function MatchesListPage() {
  const [formats, setFormats] = useState<TournamentFormatResponseDTO[]>([]);
  const [teams, setTeams] = useState<MatchTeamResponseDTO[]>([]);
  const [disciplines, setDisciplines] = useState<DisciplineResponseDTO[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategoryResponseDTO[]>([]);
  const [scores, setScores] = useState<Record<string, MatchScoreResponseDTO[]>>({});
  const [loading, setLoading] = useState(false);

  const [filterSport, setFilterSport] = useState<string | undefined>();
  const [filterSubCategory, setFilterSubCategory] = useState<string | undefined>();
  const [filterTeamInput, setFilterTeamInput] = useState("");
  const [filterTeam, setFilterTeam] = useState("");

  const [scoreModal, setScoreModal] = useState<ScoreModalState | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchAllTournamentFormats(),
      fetchAllMatchTeams(),
      disciplineService.getPaginate({ page: 1, pageSize: 100 }),
      fetchAllSubCategories(),
    ])
      .then(([formatsRes, teamsRes, disciplinesRes, subCatsRes]) => {
        const loadedFormats: TournamentFormatResponseDTO[] = formatsRes;
        setTeams(teamsRes);
        if (disciplinesRes.success && disciplinesRes.data) setDisciplines(disciplinesRes.data.data);
        setSubCategories(subCatsRes);
        setFormats(loadedFormats);

        // Cargar scores de todos los eventos que tienen partidos
        const formatsWithMatches = loadedFormats.filter(
          (f) => ((f.config as any)?.matches ?? []).length > 0,
        );
        return Promise.all(
          formatsWithMatches.map((f) =>
            matchScoreService.getByEvent(f.id).then((res) => ({
              id: f.id,
              scores: res.success && res.data ? (res.data as MatchScoreResponseDTO[]) : [],
            })),
          ),
        );
      })
      .then((results) => {
        const scoreMap: Record<string, MatchScoreResponseDTO[]> = {};
        for (const r of results) scoreMap[r.id] = r.scores;
        setScores(scoreMap);
      })
      .finally(() => setLoading(false));
  }, []);

  const reloadScores = useCallback((tournamentFormatId: string) => {
    matchScoreService.getByEvent(tournamentFormatId).then((res) => {
      if (res.success && res.data) {
        setScores((prev) => ({
          ...prev,
          [tournamentFormatId]: res.data as MatchScoreResponseDTO[],
        }));
      }
    });
  }, []);

  const reloadAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetchAllTournamentFormats(),
      fetchAllMatchTeams(),
      disciplineService.getPaginate({ page: 1, pageSize: 100 }),
      fetchAllSubCategories(),
    ])
      .then(([formatsRes, teamsRes, disciplinesRes, subCatsRes]) => {
        const loadedFormats: TournamentFormatResponseDTO[] = formatsRes;
        setTeams(teamsRes);
        if (disciplinesRes.success && disciplinesRes.data) setDisciplines(disciplinesRes.data.data);
        setSubCategories(subCatsRes);
        setFormats(loadedFormats);

        const formatsWithMatches = loadedFormats.filter(
          (f) => ((f.config as any)?.matches ?? []).length > 0,
        );
        return Promise.all(
          formatsWithMatches.map((f) =>
            matchScoreService.getByEvent(f.id).then((res) => ({
              id: f.id,
              scores: res.success && res.data ? (res.data as MatchScoreResponseDTO[]) : [],
            })),
          ),
        );
      })
      .then((results) => {
        const scoreMap: Record<string, MatchScoreResponseDTO[]> = {};
        for (const r of results) scoreMap[r.id] = r.scores;
        setScores(scoreMap);
      })
      .finally(() => setLoading(false));
  }, []);

  // Construir filas aplanadas de todos los formatos, deduplicando por equipo+fecha
  const allRows: MatchRow[] = (() => {
    const seen = new Set<string>();
    const rows: MatchRow[] = [];
    for (const f of formats) {
      const matches: any[] = (f.config as any)?.matches ?? [];
      const subCategoryId = (f.config as any)?.subCategoryId as string | undefined;
      for (let idx = 0; idx < matches.length; idx++) {
        const m = matches[idx];
        const dedupeKey = `${m.homeTeamId}|${m.awayTeamId}|${m.date}|${m.time ?? ""}`;
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);
        rows.push({
          key: `${f.id}-${idx}`,
          tournamentFormatId: f.id,
          eventName: f.name,
          sport: f.sport,
          subCategoryId,
          matchOrder: m.order ?? idx + 1,
          homeTeamId: m.homeTeamId,
          awayTeamId: m.awayTeamId,
          date: m.date,
          time: m.time,
          status: m.status,
          liveMinutes: m.liveMinutes,
        });
      }
    }
    return rows;
  })();

  const normalizedTeamFilter = normalizeSearchValue(filterTeam);

  // Aplicar filtros independientes
  const filteredRows = allRows.filter((row) => {
    if (filterSport && row.sport !== filterSport) return false;
    if (filterSubCategory && row.subCategoryId !== filterSubCategory) return false;
    if (normalizedTeamFilter) {
      const homeTeam = normalizeSearchValue(getTeamName(row.homeTeamId));
      const awayTeam = normalizeSearchValue(getTeamName(row.awayTeamId));
      if (!homeTeam.includes(normalizedTeamFilter) && !awayTeam.includes(normalizedTeamFilter)) {
        return false;
      }
    }
    return true;
  }).sort((a, b) => {
    const aTime = a.date ? dayjs(a.date).valueOf() : 0;
    const bTime = b.date ? dayjs(b.date).valueOf() : 0;
    return bTime - aTime;
  });

  function clearFilters() {
    setFilterSport(undefined);
    setFilterSubCategory(undefined);
    setFilterTeamInput("");
    setFilterTeam("");
  }

  function applyTeamFilter() {
    setFilterTeam(filterTeamInput.trim());
  }

  const hasFilters = !!(
    filterSport ||
    filterSubCategory ||
    filterTeam ||
    filterTeamInput
  );

  function getTeamName(id: string) {
    return teams.find((t) => t.id === id)?.name ?? "—";
  }

  function getScore(tournamentFormatId: string, matchOrder: number) {
    return scores[tournamentFormatId]?.find((s) => s.matchOrder === matchOrder);
  }

  function openScoreModal(row: MatchRow) {
    setScoreModal({
      tournamentFormatId: row.tournamentFormatId,
      matchOrder: row.matchOrder,
      matchLabel: `Partido #${row.matchOrder} — ${row.eventName}`,
      homeTeamName: getTeamName(row.homeTeamId),
      awayTeamName: getTeamName(row.awayTeamId),
    });
  }

  function getCategoryName(subCategoryId?: string) {
    if (!subCategoryId) return "—";
    return subCategories.find((s) => s.id === subCategoryId)?.category ?? "—";
  }

  const columns = [
    {
      title: "Local",
      key: "home",
      render: (_: any, row: MatchRow) => getTeamName(row.homeTeamId),
    },
    {
      title: "Visitante",
      key: "away",
      render: (_: any, row: MatchRow) => getTeamName(row.awayTeamId),
    },
    {
      title: "Fecha",
      key: "date",
      render: (_: any, row: MatchRow) =>
        row.date ? formatDate(new Date(row.date)) : "—",
    },
    {
      title: "Hora",
      key: "time",
      render: (_: any, row: MatchRow) =>
        row.time ? dayjs(row.time).format("hh:mm a") : "—",
    },
    {
      title: "Estado",
      key: "status",
      render: (_: any, row: MatchRow) => {
        if (row.status === 'live') {
          const label = row.liveMinutes ? `En vivo ${row.liveMinutes}'` : 'En vivo';
          return <Tag color="green">{label}</Tag>;
        }
        if (row.status === 'finished') {
          return <Tag color="default">Finalizado</Tag>;
        }
        return <Tag color="blue">Programado</Tag>;
      },
    },
    {
      title: "Score",
      key: "score",
      render: (_: any, row: MatchRow) => {
        const score = getScore(row.tournamentFormatId, row.matchOrder);
        if (!score) return <Tag color="default">Sin registrar</Tag>;
        return (
          <Tag color="blue" className="font-semibold text-sm px-3">
            {score.homeScore} - {score.awayScore}
          </Tag>
        );
      },
    },
    {
      title: "Categoría",
      key: "category",
      render: (_: any, row: MatchRow) => getCategoryName(row.subCategoryId),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 150,
      render: (_: any, row: MatchRow) => {
        const score = getScore(row.tournamentFormatId, row.matchOrder);
        return (
          <Button
            type="link"
            size="small"
            icon={<FaEdit />}
            onClick={() => openScoreModal(row)}
          >
            {score ? "Editar score" : "Registrar score"}
          </Button>
        );
      },
    },
  ];

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Partidos</h2>
          <p className="text-gray-600 mt-1">
            Consulta y registra los scores de los partidos.
          </p>
        </div>
        <Button
          type="primary"
          icon={<FaPlus />}
          onClick={() => setCreateModalOpen(true)}
        >
          Agregar Partido
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div className="min-w-[180px]">
          <p className="text-xs font-medium text-gray-500 mb-1">Deporte</p>
          <Select
            placeholder="Todos los deportes"
            allowClear
            showSearch
            value={filterSport}
            onChange={(v) => setFilterSport(v)}
            options={disciplines.map((d) => ({ label: d.name, value: d.name }))}
            className="w-full"
          />
        </div>

        <div className="min-w-[180px]">
          <p className="text-xs font-medium text-gray-500 mb-1">Categoría</p>
          <Select
            placeholder="Todas las categorías"
            allowClear
            showSearch
            filterOption={(input, opt) =>
              String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            value={filterSubCategory}
            onChange={(v) => setFilterSubCategory(v)}
            options={subCategories.map((s) => ({ label: s.category, value: s.id }))}
            className="w-full"
          />
        </div>

        <div className="min-w-[220px]">
          <p className="text-xs font-medium text-gray-500 mb-1">Equipo</p>
          <Input
            placeholder="Buscar por equipo"
            allowClear
            value={filterTeamInput}
            onChange={(event) => setFilterTeamInput(event.target.value)}
          />
        </div>

        <Button
          type="primary"
          icon={<FaSearch />}
          onClick={applyTeamFilter}
          className="self-end"
        >
          Consultar
        </Button>

        {hasFilters && (
          <Button
            type="default"
            icon={<FaTimes />}
            onClick={clearFilters}
            className="self-end"
          >
            Limpiar filtros
          </Button>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={filteredRows}
        rowKey="key"
        loading={loading}
        pagination={{ pageSize: 7, showSizeChanger: true }}
        locale={{ emptyText: "No hay partidos para mostrar." }}
      />

      {scoreModal && (
        <MatchScoreModal
          open={true}
          onClose={() => setScoreModal(null)}
          onSaved={() => reloadScores(scoreModal.tournamentFormatId)}
          tournamentFormatId={scoreModal.tournamentFormatId}
          matchOrder={scoreModal.matchOrder}
          matchLabel={scoreModal.matchLabel}
          homeTeamName={scoreModal.homeTeamName}
          awayTeamName={scoreModal.awayTeamName}
        />
      )}

      <MatchCreateModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={() => {
          setCreateModalOpen(false);
          reloadAll();
        }}
      />
    </div>
  );
}
